import { z } from 'zod';
import { formatNameWithInitials } from './person-name';
import type { VehicleRoutePointDto } from './route-points';
import { baseListQuery, dateOnlySchema, formatPhone, uuidSchema } from './common';
import type { RequestStatus, VehicleRequestType } from './enums';
import { requestStatusLabels } from './enums';
import type { DriverDocumentGap } from './persons';
import type { VehicleOwnership, VehicleSpecValues, VehicleStatus } from './vehicles';
// Только тип: слот прицепа объявлен в реестре прицепов (`vehicle-trailers`), и ответ подсказки
// обязан называть его тем же типом — второе объявление «1 | 2» разошлось бы с первым молча.
import type { TrailerHitchPosition } from './vehicle-trailers';
// Только тип: `waybill-task-rows` импортирует отсюда значения (`BLANK_WAYBILL_CONFIRM`,
// `routeRequestCapacity`), и обратная ссылка значением замкнула бы модули в цикл. Тип стирается
// компиляцией, поэтому во время выполнения этого ребра не существует вовсе.
import type { WaybillWarning } from './waybill-task-rows';
import type { WaybillFormCode, WaybillRequirement, WaybillStatus } from './waybills';
import {
  correctionFloorDateKey,
  isTypeWaybillFormCode,
  RENTAL_WAYBILL_REASON,
  UNSETTLED_TYPE_WAYBILL_REASON,
} from './waybills';
import { WAYBILL_CORRECTION_DAYS } from './time';

// ── Маршрут: рейс одной машины на одну дату (план `docs/vehicle-routes-plan.md`) ──
//
// Между заявкой и путевым листом стоит планировочный слой. Заявку кладут в маршрут переводом в
// работу, лист выписывают с маршрута отдельным действием — когда состав рейса собран. До этого
// маршрут правится свободно: заявки переставляют, водителя меняют, реквизиты рейса уточняют.
//
// Область та же, где выписывается лист 4-П (ADR 0037, ADR 0041): грузоперевозка на собственной
// технике. Арендную машину ведёт арендодатель, он же выписывает на неё лист.
//
// Заказ техники на объект маршрута не знает — машина стоит на площадке неделю, — но **доезжает**
// она туда своим ходом по городу, и на этот перегон выписывается тот же 4-П. Перегон — тоже рейс,
// только другого назначения: одна единица техники, одна заявка-основание и две строки «откуда —
// куда» вместо состава из заявок.

/**
 * Заявок в маршруте — столько, сколько их держит его бланк (ADR 0068).
 *
 * У 4-П число выбирает бумага: четыре строки таблицы «Задание водителю», каждая со своим талоном
 * заказчика, плюс три нижние строки блока «Дополнительное задание водителю», куда рейсы 5–7
 * печатаются одной строкой «откуда — куда, груз, контакты». Талона у них нет (`WAYBILL_COUPONS`).
 *
 * У формы № 3 бумага больше не решает ничего: задание в ней не печатается вовсе (ADR 0071) —
 * заявки легкового доезжают до водителя оповещением, а порядок их выполнения портал не
 * гарантирует. Десять осталось потолком, который держит CHECK позиции в базе (миграция `0096`), —
 * это «сколько заявок портал ведёт в одном рейсе», а не «сколько строк напечатает бланк».
 *
 * `esm2` — лист на неделю работы машины на площадке: рейса у него нет вовсе, он выписывается на
 * одну заявку (ADR 0060). Единица здесь значит «маршрутом такой лист не собирают».
 */
export const ROUTE_REQUEST_CAPACITY: Record<WaybillFormCode, number> = {
  '4p': 7,
  leg3: 10,
  esm2: 1,
};

/**
 * Сколько заявок держит маршрут с этим бланком. `null` — бланк не выписывается (арендная машина):
 * ёмкость берётся наибольшая, потому что запрещает такой рейс не она, а `assertRouteVehicle`, и
 * отвечать «мест нет» там, где дело не в местах, значило бы объяснять человеку не ту причину.
 */
export function routeRequestCapacity(formCode: WaybillFormCode | null): number {
  return formCode ? ROUTE_REQUEST_CAPACITY[formCode] : MAX_ROUTE_REQUESTS;
}

/**
 * Потолок по всем бланкам: им ограничены схема перестановки и CHECK позиции в базе. Сколько
 * заявок влезет в конкретный маршрут, решает его бланк — `routeRequestCapacity`.
 */
export const MAX_ROUTE_REQUESTS = Math.max(...Object.values(ROUTE_REQUEST_CAPACITY));

/**
 * Талонов заказчиков в бланке 4-П. Талон отрывной: заказчик расписывается в нём за выполненный
 * рейс. Заявки сверх четвёртой едут по строке доп. задания и подписи заказчика не собирают —
 * диспетчер, собирая маршрут, обязан это видеть. У формы № 3 талонов нет вовсе.
 */
export const WAYBILL_COUPONS = 4;

// ── Назначение рейса ──

/**
 * Зачем рейс.
 *
 * `freight` — маршрут грузоперевозки: состав из заявок, талоны заказчиков, бланк по типу ТС.
 * `delivery` и `pickup` — перегон спецтехники на объект и обратно: состава нет, есть заявка,
 * ради которой едут, и задание «откуда — куда».
 */
export const ROUTE_PURPOSES = ['freight', 'delivery', 'pickup'] as const;
export const routePurposeSchema = z.enum(ROUTE_PURPOSES);
export type RoutePurpose = (typeof ROUTE_PURPOSES)[number];

export const routePurposeLabels: Record<RoutePurpose, string> = {
  freight: 'Грузоперевозка',
  delivery: 'Доставка техники на объект',
  pickup: 'Вывоз техники с объекта',
};

/** Короткая пометка в списке рейсов: рядом с номером «Р-12» слово «Доставка» читается само. */
export const routePurposeShortLabels: Record<RoutePurpose, string> = {
  freight: 'Рейс',
  delivery: 'Доставка',
  pickup: 'Вывоз',
};

/** Перегон техники: рейс без состава, с заявкой-основанием и заданием «откуда — куда». */
export function isRelocationPurpose(purpose: RoutePurpose): boolean {
  return purpose !== 'freight';
}

/** Отображаемый номер маршрута: «Р-12» (в БД хранится только число). */
export function formatVehicleRouteNumber(num: number): string {
  return `Р-${num}`;
}

/** Разбор пользовательского ввода поиска: «12» / «Р-12» / «р-0012» → 12. */
export function parseVehicleRouteNumberSearch(input: string): number | undefined {
  const digits = input.replace(/^[рРpP]\s*-?\s*/u, '').trim();
  if (!/^\d+$/.test(digits)) return undefined;
  const num = Number(digits);
  return Number.isSafeInteger(num) && num > 0 ? num : undefined;
}

// ── Реквизиты рейса ──
// Прицеп, гаражный номер, вид сообщения и вид перевозки описывают выезд, а не заявку: от рейса к
// рейсу они почти не меняются, поэтому форма подставляет их от прошлого рейса этой машины, а
// человек правит раз в сезон. До появления маршрутов они жили в назначении заявки под именем
// `waybillFieldsSchema` (ADR 0037) — там же осталось устаревшее имя, пока его читает старое тело
// запроса на перевод в работу.

/**
 * Вид сообщения — дальность выезда тремя словами заказчика.
 *
 * Набор живёт в контрактах, а не в окне рейса: значение печатается в графе «Вид сообщения»
 * бланков 4-П и формы № 3, то есть уходит из портала бумагой наружу, а заводят и правят рейс три
 * разных окна — назначение заявки, правка маршрута и коррекция. Свой список у каждого означал бы
 * «междугороднее» в одном листе и «междугородное» в соседнем, и пачку за месяц было бы не
 * сверить. Написание здесь ровно то, которым называет графу заказчик, и правится оно одним местом.
 */
export const COMMUNICATION_KINDS = ['городское', 'пригородное', 'междугороднее'] as const;
export type CommunicationKind = (typeof COMMUNICATION_KINDS)[number];

/**
 * Что стоит в поле, когда рейс только заводят: возят в основном за город, но в пределах области.
 * Подставленное значение здесь безопасно — оно из набора, и печатается им то же, что печаталось
 * бы выбранным рукой.
 */
export const DEFAULT_COMMUNICATION_KIND: CommunicationKind = 'пригородное';

/**
 * Вид сообщения перегона: технику возят с базы на площадку и обратно, и графа у такого рейса от
 * раза к разу одна. Портал подставляет её сам — окно перегона про вид сообщения не спрашивает, —
 * поэтому значение названо здесь: литерал в окне разошёлся бы с набором молча, а увидели бы это
 * уже на напечатанном листе.
 */
export const RELOCATION_COMMUNICATION_KIND: CommunicationKind = 'городское';

/**
 * Опции поля «вид сообщения» для окна: набор плюс собственное значение рейса, если оно из набора
 * выпало. Выпавшие есть и будут: до появления списка графа была свободной строкой, и в рейсах
 * прошлых месяцев стоит что угодно — от пустой до «междугородное» с одной «н». Списка без такого
 * значения хватило бы, чтобы правка соседнего поля молча унесла графу, которая на выданном листе
 * уже напечатана: портал разошёлся бы с бумагой у водителя в руках.
 *
 * Пустое значение опцией не становится: очистить графу через окно нельзя (обязательность просили
 * на уровне UI), и пункт «не выбрано» предлагал бы ровно то, чего окно больше не позволяет.
 */
export function communicationKindOptions(
  current?: string | null,
): { value: string; label: string }[] {
  const kinds: string[] = [...COMMUNICATION_KINDS];
  const own = (current ?? '').trim();
  if (own && !kinds.includes(own)) kinds.push(own);
  return kinds.map((kind) => ({ value: kind, label: kind }));
}

export const routeTripFieldsSchema = z
  .object({
    /**
     * Рейс с прицепом. Признак рейса, а не свойство машины: прицепа в реестре техники нет, а
     * требование к категории водителя он поднимает — «C» превращается в «CE».
     */
    withTrailer: z.boolean().optional().default(false),
    trailer1Model: z.string().trim().max(100).optional().default(''),
    trailer1RegNumber: z.string().trim().max(20).optional().default(''),
    trailer2Model: z.string().trim().max(100).optional().default(''),
    trailer2RegNumber: z.string().trim().max(20).optional().default(''),
    /** Гаражный номер машины: если пуст, берётся из справочника техники. */
    garageNumber: z.string().trim().max(50).optional().default(''),
    /**
     * Вид сообщения: одно из `COMMUNICATION_KINDS` — окна спрашивают его списком и пустым в
     * портал не пускают.
     *
     * Схема при этом остаётся свободной строкой намеренно, и ужесточать её до `z.enum` нельзя: у
     * рейсов, заведённых до списка, графа пуста или написана по-своему, а тело правки уходит
     * целиком — enum отбил бы такому рейсу и смену водителя, и перенос дня, пока кто-то не
     * перепишет графу. Обязательность заказчик просил на уровне UI: список отвечает за то, что
     * портал печатает впредь, а не за то, что уже напечатано.
     */
    communicationKind: z.string().trim().max(50).optional().default(''),
    /** Вид перевозки: в образцах бланка — «коммерческая». */
    transportationKind: z.string().trim().max(50).optional().default(''),
  })
  .strict()
  .refine(
    (w) =>
      w.withTrailer ||
      (!w.trailer1Model && !w.trailer1RegNumber && !w.trailer2Model && !w.trailer2RegNumber),
    { message: 'Реквизиты прицепа без прицепа в рейсе не печатаются', path: ['withTrailer'] },
  )
  /*
   * Порядок пар — свойство бланка, а не прихоть формы: в 4-П графы «Прицеп 1» и «Прицеп 2» стоят
   * друг под другом, и заполненная вторая при пустой первой печатается дырой посреди шапки.
   * Второй прицеп без первого — это не «второй», это первый, записанный не туда.
   */
  .refine(
    (w) => !!(w.trailer1Model || w.trailer1RegNumber) || (!w.trailer2Model && !w.trailer2RegNumber),
    {
      message: 'Второй прицеп не печатается без первого — заполните графы прицепа 1',
      path: ['trailer2Model'],
    },
  );
export type RouteTripFields = z.infer<typeof routeTripFieldsSchema>;

/**
 * Подпись прицепов рейса: «СЗАП-8551 АВ123477 · ШМИТЦ SPR-24 ВХ933277».
 *
 * Одна функция на все места, где подпись показывают, — карточку рейса и кабинет водителя
 * (`vehicle_routes`), журнал путевых листов (`waybills`) и задание по листу ЭСМ-2. До неё то же
 * выражение было переписано трижды, и все три копии знали только первый прицеп — при том, что и
 * графы бланка, и колонки БД, и снимок листа держат два (ADR 0037 п. 8). Расхождение не
 * проявлялось лишь потому, что второй прицеп негде было ввести: открыть графу, оставив три копии,
 * значило бы показать водителю половину состава — и показать по-разному в трёх местах.
 *
 * Разделители разные, и это не косметика. Внутри прицепа марка и госномер идут через пробел: так
 * они стоят в бланке двумя соседними графами и так их читают вслух. Между прицепами — « · », тот
 * же разделитель, каким подпись машины отделяет марку от госномера. Пробел на обеих границах
 * склеил бы четыре слова в строку, из которой не видно, где кончается один прицеп.
 */
export function trailerLabelOf(fields: {
  trailer1Model?: string | null;
  trailer1RegNumber?: string | null;
  trailer2Model?: string | null;
  trailer2RegNumber?: string | null;
}): string {
  const one = (model?: string | null, reg?: string | null): string =>
    [model, reg]
      .map((v) => (v ?? '').trim())
      .filter(Boolean)
      .join(' ');
  return [
    one(fields.trailer1Model, fields.trailer1RegNumber),
    one(fields.trailer2Model, fields.trailer2RegNumber),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What the caption says when the trip is ticked "with trailer" but the trailer boxes are empty. */
export const TRAILER_DETAILS_MISSING_CAPTION = 'с прицепом — реквизиты не указаны';

/**
 * The "with trailer" caption under a vehicle: in the waybill journal, the route window and the list
 * of a vehicle's routes. `null` when the trip goes without a trailer — nothing is shown then.
 *
 * One function for the three places because the bare caption lied in all three the same way. It
 * was drawn by the checkbox alone, so a trip ticked "with trailer" whose boxes are empty printed
 * "с прицепом " with nothing after it, which reads as "the sheet names a trailer". The checkbox and
 * the boxes are independent (the issue warning `trailer_graphs_blank` exists for exactly this
 * case), and the caption must say which of them is missing instead of hiding it.
 *
 * Takes the label, not the four boxes: the callers get it from the DTO, built by `trailerLabelOf`
 * on the server, and a second assembly here would be a second answer to "what is the trailer".
 */
export function trailerCaption(source: {
  withTrailer: boolean;
  trailerLabel: string | null;
}): string | null {
  if (!source.withTrailer) return null;
  const label = (source.trailerLabel ?? '').trim();
  return label ? `с прицепом ${label}` : TRAILER_DETAILS_MISSING_CAPTION;
}

// ── DTO ──

/** Заявка в составе рейса: талон будущего листа плюс то, что показывает карточка маршрута. */
export interface VehicleRouteRequestDto {
  requestId: string;
  /** «ТС-123» — номер заявки, как его читают в портале. */
  displayNumber: string;
  /** Позиция в рейсе, 1..4; она же `slot` талона заказчика в бланке. */
  position: number;
  /**
   * День линейного заказа, ради которого строка стоит в рейсе (ADR 0100 §2, миграция 0127);
   * `null` — грузоперевозка: день несёт сам рейс, и своего дня у строки нет.
   *
   * Строка линейного дня читается в составе иначе, чем грузовая: у заказа техники на объект нет ни
   * погрузки с разгрузкой, ни тонн — есть заказчик (`customerName`) и день. Поле необязательное
   * ровно поэтому: грузовая строка дня не несёт, и требовать от собирающего состав явного «дня
   * нет» значило бы заставлять отвечать на вопрос, которого у неё не возникает.
   */
  workDate?: string | null;
  /**
   * Статус заявки: отменённая или закрытая остаётся в маршруте историей рейса (лист уже выписан),
   * но новый лист по такому составу не выписывается — см. `canIssueWaybill`.
   */
  status: RequestStatus;
  /** Заказчик: объект строительства или отдел (ADR 0040). */
  customerName: string;
  loadingLocation: string;
  unloadingLocation: string;
  scheduledAt: string;
  scheduledTimeUnspecified: boolean;
  /** «12 м³» либо «8 т» — тем же правилом, которым груз печатается в бланке. */
  cargoLabel: string;
}

/** Лист, выписанный по рейсу: действующий, а если его нет — последний аннулированный. */
export interface VehicleRouteWaybillDto {
  id: string;
  /** «260604-646-00000004897» — как номер напечатан на бланке. */
  number: string;
  status: WaybillStatus;
  issuedForDate: string;
}

/** Заявка, ради которой едет перегон: у рейса перемещения она вместо состава. */
export interface VehicleRouteSourceRequestDto {
  requestId: string;
  /** «ТС-501» — номер заявки, как его читают в портале. */
  displayNumber: string;
  status: RequestStatus;
  /** Заказчик: объект строительства или отдел (ADR 0040). */
  customerName: string;
}

export interface VehicleRouteDto {
  id: string;
  /** «Р-12» — по нему о рейсе говорят по телефону. */
  displayNumber: string;
  /** Зачем рейс: грузоперевозка либо перегон техники на объект и обратно. */
  purpose: RoutePurpose;
  /**
   * Бланк, по которому выпишется лист: у грузового рейса — закреплённый за типом машины, у
   * перегона — всегда 4-П. `null` — лист не выписывается вовсе (тип без бланка). Портал спрашивает
   * его не ради документа: у формы № 3 нет граф прицепа, и вводить их реквизиты незачем.
   */
  formCode: WaybillFormCode | null;
  routeDate: string;
  vehicleId: string;
  /** «КамАЗ 65201 · Е646СК799» — чем едут. */
  vehicleLabel: string;
  /**
   * Вид и тип машины рейса и ТТХ её категории. Ими заявка сверяет заказанное с тем, чем рейс
   * поедет (ADR 0059, ADR 0064): подсказка рейсов не сужена ни типом, ни видом — она помечает
   * каждый рейс (заказанный тип, крупнее, меньше, другой вид) и этим же порядком стоит. Сравнение
   * считает портал правилом из контрактов (`vehicleSubstitutionOf`): сервер здесь ничего не
   * решает и ничего не запрещает.
   */
  vehicleKindId: string;
  vehicleTypeId: string;
  vehicleTypeName: string;
  vehicleCategoryId: string | null;
  vehicleCategorySpecs: VehicleSpecValues | null;
  /** Пусто — водителя ещё не назначили: маршрут собирают заранее, человека ставят утром. */
  driverPersonId: string | null;
  driverName: string;
  /**
   * Чего не хватает назначенному водителю для листа на дату рейса (ADR 0064); пусто — комплект
   * полный либо водителя ещё нет. Выписку не останавливает: по нему карточка предупреждает о
   * графах, которые в бланке останутся пустыми, — до печати, а не после.
   */
  driverGaps: DriverDocumentGap[];
  withTrailer: boolean;
  trailerLabel: string;
  trailer1Model: string;
  trailer1RegNumber: string;
  trailer2Model: string;
  trailer2RegNumber: string;
  garageNumber: string;
  communicationKind: string;
  transportationKind: string;
  comment: string;
  /** Состав грузового рейса; у перегона пуст — талонов заказчиков там не бывает. */
  requests: VehicleRouteRequestDto[];
  /**
   * Порядок объезда: точки со своими ролями (план `docs/route-trips-plan.md`, Р4, §6).
   *
   * Полем DTO, а не пересечением типа в ручке, как это было заведено «на время»: точки — не
   * приложение к рейсу, а то, чем он теперь и является. Печать задания идёт по их позициям (Р11),
   * карточка считает по ним подсказки и блокеры, а состав отвечает лишь на «чьи заявки в рейсе».
   * Пока точек не было в контракте, сервер и портал склеивали их пересечением каждый у себя — и
   * тест, собравший рейс честным `VehicleRouteDto`, точек не имел вовсе.
   *
   * У перегона список пуст: состава у него нет, задание дают колонки самого рейса (миграция 0082).
   */
  points: VehicleRoutePointDto[];
  /** Заявка-основание перегона; у грузового рейса `null` — его основание это состав. */
  sourceRequest: VehicleRouteSourceRequestDto | null;
  /** Задание перегона: откуда и куда едет техника. У грузового рейса пусто. */
  moveFrom: string;
  moveTo: string;
  waybill: VehicleRouteWaybillDto | null;
  createdByName: string;
  createdAt: string;
  version: number;
}

/**
 * Маршрут в карточке и списке заявок: номер рейса, позиция в нём и есть ли лист. Несколькими
 * полями, а не целым `VehicleRouteDto`: списку заявок нужна колонка, а не состав чужого рейса.
 */
export interface VehicleRequestRouteDto {
  id: string;
  displayNumber: string;
  /**
   * День рейса. Заявке он не свой — свой у неё день подачи, — но они обязаны совпадать
   * (`canJoinRoute`), и разойтись могут только правкой одной из сторон. По этому полю портал и
   * ловит расхождение (`routeDateMismatch`): без него «заявка уехала на завтра, а рейс остался
   * на сегодня» обнаруживалось бы у принтера.
   */
  routeDate: string;
  position: number;
  hasWaybill: boolean;
  /**
   * Версия рейса — ею перенос заявки опознаёт исходный маршрут (`attachRouteRequestSchema.source`).
   * Без неё пришлось бы грузить рейс отдельным запросом перед каждым переносом, а между чтением и
   * отправкой рейс успевал бы измениться — и оптимистическая блокировка проверяла бы устаревшее.
   */
  version: number;
}

// ── Ответ подсказки заведения рейса ──

/**
 * Прицеп, закреплённый за машиной, — элемент поля `hitched` ответа `GET /vehicle-routes/suggest`
 * (план `docs/vehicle-trailers-plan.md`, §4.2.2, §4.2.3).
 *
 * Полей ровно столько, сколько нужно окну, чтобы заполнить графы бланка и подписать их источник.
 * Марка и госномер — это и есть графы «Прицеп N (марка)» и «(гос. номер)»; `position` говорит, в
 * какую из двух пар их класть; `status` нужен подписи: закрепление прицепа в ремонте законно
 * (§4.2.3 — он физически стоит за тягачом), но подставленный `maintenance` окно обязано назвать
 * вслух, иначе портал молча поставит в рейс то, что стоит в боксе.
 *
 * Карточка прицепа целиком (`VehicleTrailerDto`) здесь не отдаётся намеренно: окну заведения рейса
 * незачем ни массы по СТС, ни собственник, ни примечание, а лишнее поле в подсказке — это лишнее
 * поле, которое однажды покажут.
 */
export interface HitchedTrailerDto {
  id: string;
  /** Слот бланка 4-П: 1 — первая пара граф, 2 — вторая. Тот же номер, что и в реестре прицепов. */
  position: TrailerHitchPosition;
  /** Марка строкой: у прицепа её и хранят строкой — ссылки на справочник моделей у него нет. */
  model: string;
  registrationNumber: string;
  /** Состояние прицепа: `maintenance` подставляется, но с предупреждением (§4.2.3). */
  status: VehicleStatus;
}

/** The registry's current hitch fills the paper's slots in order, even if only slot 2 is occupied. */
export function hitchedTrailerGraphs(
  hitched:
    | readonly Pick<HitchedTrailerDto, 'position' | 'model' | 'registrationNumber'>[]
    | null
    | undefined,
): {
  withTrailer: boolean;
  trailer1Model: string;
  trailer1RegNumber: string;
  trailer2Model: string;
  trailer2RegNumber: string;
} | null {
  const slots = [...(hitched ?? [])].sort((a, b) => a.position - b.position);
  if (slots.length === 0) return null;
  return {
    withTrailer: true,
    trailer1Model: slots[0]?.model ?? '',
    trailer1RegNumber: slots[0]?.registrationNumber ?? '',
    trailer2Model: slots[1]?.model ?? '',
    trailer2RegNumber: slots[1]?.registrationNumber ?? '',
  };
}

/**
 * Ответ `GET /vehicle-routes/suggest`: что портал знает об этой машине на этот день.
 *
 * ПОЧЕМУ ЗАКРЕПЛЁННЫЕ ПРИЦЕПЫ — ОТДЕЛЬНОЕ ПОЛЕ, А НЕ ГРАФЫ ВНУТРИ `trip`. Соблазн был подмешать
 * закрепление в `trip`: одно поле, одно правило чтения, и окнам вообще нечего менять. Цена такой
 * красоты — история прицепа поехала бы туда, где её сегодня нет. `trip` — это графы шапки
 * **прошлого рейса машины** (`lastTripFields`), и читают его не все окна заведения рейса, а два из
 * шести; окно «Новый маршрут» (`VehicleRoutesModal`) сегодня не подставляет **ничего**. Смешай
 * источники — и оно начало бы предлагать вчерашний прицеп, то есть портал стал бы решать за
 * человека там, где вчера молчал. Ровно это запретил ADR 0083 решением 2 («ни вчерашним на этой
 * машине»), и оправдания «оно и так уже было в соседнем окне» ему недостаточно: было — в двух, а
 * стало бы — в шести.
 *
 * Поэтому граница проведена по источнику, а не по окну: `hitched` — это **сегодняшнее закрепление
 * из реестра прицепов**, решение, которое человек уже принял в карточке прицепа, а `trip` остался
 * тем же, чем был. Правило чтения одно на все окна: `hitched` не пуст — графы прицепа берутся
 * оттуда и подписываются «закреплён за машиной»; пуст — новой подстановки нет вовсе, окно ведёт
 * себя ровно как вчера.
 */
export interface VehicleRouteSuggestDto {
  /** Рейсы этой машины на эту дату: ими форма предлагает готовый рейс вместо второго. */
  routes: VehicleRouteDto[];
  /**
   * Графы шапки прошлого рейса машины — гаражный номер, вид сообщения и перевозки, прицепы.
   * `null` — рейсов у машины ещё не было. Поле не изменилось ни на байт: его по-прежнему считает
   * `lastTripFields`, и появление `hitched` рядом на него не влияет.
   */
  trip: RouteTripFields | null;
  /**
   * Закреплённые за машиной прицепы, по возрастанию слота. Пусто — за машиной не закреплено
   * ничего, и это не «данных нет», а полноценный ответ: новой подстановки у такой машины не
   * бывает.
   */
  hitched: HitchedTrailerDto[];
}

// ── Правила ──

/**
 * Можно ли править маршрут. Выписанный лист замораживает состав, порядок, водителя и реквизиты
 * рейса: бланк уже у водителя, и запись, разошедшаяся с бумагой на руках, хуже отсутствия записи
 * (ADR 0037 п. 9). Аннулированный лист не мешает — испорченный бланк списывают и рейс пересобирают.
 *
 * Движения заявки вперёд по циклу правило не касается: заявку закрывают и отменяют независимо от
 * того, что с листом, иначе заявку с выписанным листом было бы нечем закрыть в день рейса. Назад —
 * касается: возврат в «Новую» стирает работу, а стереть её у заявки, стоящей в выданном бланке,
 * значило бы разойтись с бумагой на руках (`ROLLBACK_WAYBILL_MESSAGE`).
 */
export function isRouteEditable(waybillStatus: WaybillStatus | null): boolean {
  return waybillStatus === null || waybillStatus === 'cancelled';
}

export const ROUTE_FROZEN_MESSAGE =
  'По маршруту выписан путевой лист — аннулируйте его, чтобы править рейс';

/**
 * Отказ вернуть в «Новую» заявку с выписанным листом. Отмене лист не мешает — отменённая заявка
 * остаётся талоном выданного бланка, — а возврат в работу-с-нуля мешает: заявка снова пойдёт в
 * чей-то рейс, и одна и та же работа окажется сразу в двух действующих документах (ADR 0050).
 */
export const ROLLBACK_WAYBILL_MESSAGE =
  'По заявке выписан действующий путевой лист — аннулируйте его, чтобы вернуть заявку в «Новую»';

/**
 * Предупреждение о расхождении дат: заявку правят, а она лежит в рейсе другого дня.
 *
 * Такое расхождение портал не запрещает — заявку и рейс правят разные люди в разное время, — но и
 * молчать о нём нельзя: рейс останется на прежнем дне, а лист напечатает задание, которого в этот
 * день уже нет. Человека отправляют туда, где это чинится, — в карточку маршрута.
 */
export function routeDateMismatch(
  request: {
    /**
     * День, на который подана заявка; `null` — своего дня у неё нет.
     *
     * Пусто отвечает линейный заказ (ADR 0100): его строка в рейсе несёт день, физически равный
     * дню рейса, — это составной внешний ключ `(route_id, work_date) → vehicle_routes (id,
     * route_date)` с `ON UPDATE CASCADE` (миграция 0127). Перенос рейса уносит день вместе с
     * собой, а забыть его сервер не может: база не даст. Расхождению взяться неоткуда, и молчание
     * здесь не послабление, а единственный правдивый ответ.
     */
    tripDate: string | null;
  },
  route: { displayNumber: string; routeDate: string },
): string | null {
  if (request.tripDate === null) return null;
  if (request.tripDate === route.routeDate) return null;
  return `Заявка теперь на ${request.tripDate}, а маршрут ${route.displayNumber} заведён на ${route.routeDate}. Лист печатает задание на день рейса — перенесите маршрут или выньте из него заявку.`;
}

/**
 * The route's issue form: freight uses its vehicle type, while relocation always uses 4-P
 * (ADR 0037, ADR 0065). A rental vehicle's document belongs to its lessor.
 *
 * Until the pending CHECK ships, an old type may still hold 'esm2' in the database (ADR 0128).
 * Such a freight route has no issuable form; guessing 4-P would change the controlled document.
 * The refusal is named for the person who maintains the type, while relocation remains 4-P.
 */
export function routeWaybillForm(input: {
  purpose: RoutePurpose;
  ownership: VehicleOwnership;
  /** The stored type form affects freight only; relocation always uses 4-P. */
  formCode: WaybillFormCode;
}): WaybillRequirement {
  if (input.ownership !== 'own') {
    return { formCode: null, reason: RENTAL_WAYBILL_REASON };
  }
  if (isRelocationPurpose(input.purpose)) return { formCode: '4p', reason: null };
  if (!isTypeWaybillFormCode(input.formCode)) {
    return { formCode: null, reason: UNSETTLED_TYPE_WAYBILL_REASON };
  }
  return { formCode: input.formCode, reason: null };
}

export const ROUTE_LEGACY_WAYBILL_MESSAGE =
  'По заявке уже выписан путевой лист вне маршрутов — аннулируйте его или дождитесь переноса истории';

/** Что не так с заявкой для этого рейса; `{ ok: true }` — годится. */
export type RouteJoinCheck = { ok: true } | { ok: false; reason: string };

/**
 * Чем заявка отвечает на вопрос «в какой день она едет»: точкой или отрезком.
 *
 * У грузоперевозки день один и известен заранее — его несёт время подачи, и заявка соседнего дня
 * напечатала бы в листе рейс, которого в этот день не было. У линейного заказа (ADR 0100 §2)
 * своего дня нет вовсе: есть срок работ, внутри которого машина выходит день за днём, и в рейс
 * кладут **один** его день.
 *
 * Прежнего `tripDate: string` здесь нет намеренно: одно поле заставило бы линейный заказ придумать
 * себе единственный день — а заведён он ровно ради того, что дней у него много. Какой именно день
 * кладут, линейный ответ не несёт тоже, и это не пропуск: день строки состава физически равен дню
 * рейса (составной FK, миграция 0127), и второй ответ на «какой это день» разошёлся бы с первым.
 */
export type RouteJoinDay =
  /** Грузоперевозка: день подачи по МСК, `YYYY-MM-DD`. */
  | { kind: 'trip'; date: string }
  /**
   * Заказ техники на объект: срок работ (`dateTo` пуст у однодневного) и дни, уже стоящие в его
   * рейсах. Ими правило и отвечает на два вопроса дня: внутри ли он срока и не распланирован ли уже.
   *
   * Имя `linear` осталось от ADR 0100, где дни завела линейная техника; сегодня такой ответ даёт
   * любой заказ на объект. Переименование тронуло бы все вызывающие и ничего бы не объяснило —
   * объясняет этот комментарий.
   */
  | { kind: 'linear'; dateFrom: string; dateTo: string | null; plannedDays: readonly string[] };

/**
 * Отказ поставить в рейс день, который уже распланирован. Текст один на два правила —
 * `canJoinRoute` и `planDayBlocker` (`vehicle-request-days.ts`): спрашивают они с разных сторон
 * (рейс и карточка заявки), а отвечать обязаны одинаково — иначе человек прочтёт две разные
 * причины одного отказа. В базе это же правило держит частичный
 * `vehicle_route_requests_request_day_unique` (миграция 0127).
 */
export function dayAlreadyPlannedMessage(day: string): string {
  return `День ${day} уже стоит в рейсе этой заявки — снимите его оттуда или выберите другой день`;
}

/**
 * Отказ положить заказ техники на объект в рейс со стороны рейса (ADR 0100 §8). Дверь у дня одна —
 * карточка заявки: день знает свой срок и свою заявку, а из карточки маршрута ту же заявку
 * пришлось бы разыскивать по объекту среди всех работающих.
 *
 * Про линейность текст молчит намеренно: дни ведутся у любого заказа на объект (см. шапку
 * `vehicle-request-days.ts`), и обещание «это только про линейный» отправило бы половину
 * диспетчеров искать несуществующий запрет вместо той самой карточки.
 */
export const LINEAR_DAY_DOOR_MESSAGE =
  'Дни заказа техники на объект планируют из карточки заявки: рейс не знает, какой день срока в него ставят';

/**
 * Годится ли заявка для маршрута.
 *
 * Порядок проверок задаёт и то, что прочитает человек: сначала «такие заявки в рейс не ходят»
 * (назначение рейса, вид заявки), потом состояние самой заявки и принадлежность машины, потом
 * день, и только потом — вместимость.
 *
 * С ADR 0100 отказ виду заявки перестал быть безусловным: в рейс ходит и заказ техники на объект —
 * не заявкой целиком, а одним днём срока. День у грузоперевозки сверяется на равенство (лист
 * печатает задание на день), у заказа на объект — на попадание в срок и на то, что этот день ещё
 * не занят другим рейсом.
 */
export function canJoinRoute(
  request: {
    requestType: VehicleRequestType;
    /**
     * Линейный ли **заказанный** тип (ADR 0100 §1). Это правило его НЕ ЧИТАЕТ: в рейс попадает
     * день любого заказа на объект, и выбирать по типу здесь больше нечего. Поле осталось в
     * аргументе, потому что его передают все вызывающие, и убирать его значило бы править их
     * заодно; смысл признака живёт там, где по нему и правда расходится поведение, — в режиме
     * ведения заявки и в выписке недельных ЭСМ-2.
     */
    isLinear: boolean;
    status: RequestStatus;
    deletedAt: string | null;
    /** Чем заявка отвечает на вопрос о дне: точкой подачи либо сроком заказа на объект. */
    day: RouteJoinDay;
    /** Принадлежность назначенной машины; `null` — машина ещё не назначена. */
    ownership: VehicleOwnership | null;
  },
  route: {
    routeDate: string;
    requestCount: number;
    purpose: RoutePurpose;
    /** Бланк рейса: им задана вместимость — у 4-П семь строк задания, у формы № 3 десять. */
    formCode: WaybillFormCode | null;
  },
): RouteJoinCheck {
  // Перегон везёт одну единицу техники по одной заявке-основанию, и состава у него нет вовсе:
  // задание из нескольких строк — про грузоперевозку, где машина за смену объезжает несколько
  // площадок. День заказа на объект в перегон не кладут по той же причине (ADR 0100 §9): перегон
  // едет по своему основанию, а день работ — это обычный рейс дня.
  if (isRelocationPurpose(route.purpose)) {
    return {
      ok: false,
      reason:
        request.day.kind === 'linear'
          ? 'Это перегон техники — день работ в него не кладут: перегон едет по своей заявке-основанию, а день ставят в обычный рейс'
          : 'Это перегон техники — заявки в него не кладут, он едет по своей одной',
    };
  }
  /*
   * Кто вообще ходит в рейс. Грузоперевозка ходит целиком: рейс и есть её работа. Заказ техники на
   * объект — только днём срока, и дверь к этому дню одна: карточка заявки (ADR 0100 §8).
   *
   * Текст здесь один. Прежде их было два — по признаку линейности, — но после того, как дни
   * открылись любому заказу на объект, обе половины стали ложью: «в рейс ходят только
   * грузоперевозки» неправда (ходит и день такого заказа), а «это про линейный» неправда тоже
   * (тип больше ничего не решает). Одна ложь вместо двух не лучше — остался ответ по существу:
   * дверь не эта.
   */
  if (request.day.kind === 'trip' && request.requestType !== 'freight_transport') {
    return { ok: false, reason: LINEAR_DAY_DOOR_MESSAGE };
  }
  if (request.deletedAt) return { ok: false, reason: 'Заявка удалена' };
  if (request.status !== 'confirmed') {
    return {
      ok: false,
      reason: `Заявка в статусе «${requestStatusLabels[request.status]}» — в рейс кладут заявку в работе`,
    };
  }
  if (request.ownership !== 'own') {
    return {
      ok: false,
      reason:
        request.ownership === null
          ? 'На заявку не назначена техника'
          : 'Путевой лист на арендную технику выписывает арендодатель — маршрут ей не ведётся',
    };
  }
  if (request.day.kind === 'trip') {
    if (request.day.date !== route.routeDate) {
      return {
        ok: false,
        reason: `Заявка подаётся ${request.day.date}, а маршрут заведён на ${route.routeDate}`,
      };
    }
  } else {
    // День линейного заказа — это день рейса: спрашивать у заявки, какой день кладут, незачем,
    // она отвечает лишь «мой ли он». Пустой `dateTo` — однодневный срок (так его читает весь
    // модуль смен, `shiftDaysOf`).
    const lastDay = request.day.dateTo || request.day.dateFrom;
    if (
      !request.day.dateFrom ||
      route.routeDate < request.day.dateFrom ||
      route.routeDate > lastDay
    ) {
      return {
        ok: false,
        reason: `Рейс на ${route.routeDate} вне срока заказа (${request.day.dateFrom} — ${lastDay}) — в рейс кладут день работ`,
      };
    }
    if (request.day.plannedDays.includes(route.routeDate)) {
      return { ok: false, reason: dayAlreadyPlannedMessage(route.routeDate) };
    }
  }
  const capacity = routeRequestCapacity(route.formCode);
  if (route.requestCount >= capacity) {
    return {
      ok: false,
      reason: `${capacityLimit(route.formCode, capacity)} — заведите второй маршрут`,
    };
  }
  return { ok: true };
}

/**
 * Чем ограничена вместимость рейса — так, как это читает человек. У 4-П её задаёт бумага: сверх
 * седьмой заявки печатать задание некуда. У формы № 3 задание не печатается вовсе (ADR 0071), и
 * ссылаться на строки бланка стало неправдой — предел там портальный.
 */
function capacityLimit(formCode: WaybillFormCode | null, capacity: number): string {
  return formCode === 'leg3'
    ? `В рейсе легкового ${capacity} мест`
    : `В листе ${capacity} строк задания`;
}

/**
 * Вынимать ли заявку из маршрута при смене её статуса.
 *
 * «Выполнена» состав не трогает: рейс состоялся, и связь заявки с маршрутом — история, а не план.
 * Отмена и возврат в «Новую» заявку вынимают — рейса не будет, держать её в плане незачем, — но
 * только пока маршрут не заморожен выписанным листом: из бумаги, которая уже у водителя, заявка
 * исчезнуть не может.
 */
export function shouldDetachOnStatus(next: RequestStatus, frozen: boolean): boolean {
  if (frozen) return false;
  return next === 'cancelled' || next === 'new';
}

/** Готов ли рейс к выписке листа; `blocking` — номера заявок, из-за которых лист не выписать. */
export type IssueWaybillCheck =
  { ok: true } | { ok: false; reason: string; blocking: readonly string[] };

/**
 * Отказ выписать лист по рейсу без заявок. Право `waybills.issueBlank` его снимает (ADR 0071) —
 * поэтому текст называет причину, а не запрет: у того, кто такие бланки не выписывает, пустой
 * рейс это чаще всего забытый состав.
 */
export const BLANK_WAYBILL_DENIED = 'В маршруте нет заявок — лист выписывать не на что';

/**
 * Подтверждение выписки пустого бланка. Спрашивается всегда, а не только при пробелах в
 * документах водителя: номер строгой отчётности расходуется на лист, задание в котором портал не
 * печатает вовсе, и вернуть его можно только аннулированием.
 */
export const BLANK_WAYBILL_CONFIRM =
  'В маршруте нет заявок: лист выпишется с машиной, водителем и датой, но с пустым заданием — его вписывают от руки.';

/**
 * Можно ли выписать лист по этому рейсу.
 *
 * Отдельно от `isRouteEditable` проверяются статусы заявок: заявку отменили, пока маршрут был
 * заморожен (она осталась талоном выданного бланка), лист аннулировали, маршрут разморозился — и
 * без этой проверки следующая выписка молча внесла бы отменённую заявку в новый документ. То же и
 * с закрытой: «Выполнена» в маршруте — история состоявшегося рейса, а не задание на новый.
 *
 * THE FORM'S CAPACITY IS NOT ASKED HERE (ADR 0108, 12.10.2026). It used to be, by counting
 * requests, while the paper is measured in task rows — trips plus linear days (R11). A request
 * takes at least one row, so the request count never refused anything the row count
 * (`capacityBlocker` inside `routeIssueBlockers`) would let through; it only answered the same
 * question with a second number and a second wording. Every caller still meets the row count
 * before a number is spent: the issue point refuses with the `capacity_exceeded` blocker under its
 * locks, and the correction preflight asks `capacityBlocker` of the prospective rows before the
 * first cancellation.
 */
export function canIssueWaybill(route: {
  purpose: RoutePurpose;
  driverPersonId: string | null;
  /**
   * Коррекция задним числом (ADR 0101, Р36): лист выписывается **взамен** действующего, и тот
   * аннулируется этой же транзакцией.
   *
   * Снимает ровно одну проверку — «по маршруту уже есть действующий лист». Требование `confirmed`
   * у состава остаётся (Р3), и это главное, что здесь не меняется: обычная выписка спрашивает его
   * намеренно, а «Новая» заявка в бланке означала бы бумагу на работу, которую никто не поручал.
   * Возврат заявки в «Новую» при замороженном рейсе её из состава не вынимает
   * (`shouldDetachOnStatus`), так что `new` в составе вчерашнего рейса — штатное последствие
   * ADR 0058, а не редкость.
   *
   * Called before the first cancellation, over the **prospective** route (vehicle, driver,
   * composition): learning of a refusal after the number is burnt is the worst possible order. The
   * prospective form's capacity — another vehicle type may carry another form (ADR 0068) — is asked
   * next to this call by the row count (`capacityBlocker`), not here.
   */
  correction?: { allowed: true };
  /**
   * Разрешено ли выписать лист по рейсу без заявок (право `waybills.issueBlank`, ADR 0071).
   * Пустым остаётся только задание: машина, водитель и дата — реквизиты самого рейса, и без них
   * лист не выписывается никому.
   */
  blankAllowed?: boolean;
  requests: readonly { displayNumber: string; status: RequestStatus }[];
  /** Заявка-основание перегона: у грузового рейса её нет, у перегона она вместо состава. */
  sourceRequest: { displayNumber: string; status: RequestStatus } | null;
  /** Лист, уже выписанный по этому рейсу: действующий выписать второй не даёт. */
  waybillStatus: WaybillStatus | null;
}): IssueWaybillCheck {
  if (!isRouteEditable(route.waybillStatus) && route.correction?.allowed !== true) {
    return { ok: false, reason: 'По маршруту уже выписан действующий путевой лист', blocking: [] };
  }
  if (!route.driverPersonId) {
    return {
      ok: false,
      reason: 'Назначьте водителя — он обязательный реквизит листа',
      blocking: [],
    };
  }
  /*
   * У перегона состава нет: вместо талонов заказчиков он держит заявку, ради которой едет. Её
   * состояние и проверяется — отменённая означает, что технику никуда не повезут, а «Новая» —
   * что заявку откатили и машина с неё снята. «Выполнена» перегону не мешает: технику вывозят
   * с объекта и после того, как работы закрыли.
   */
  if (isRelocationPurpose(route.purpose)) {
    const source = route.sourceRequest;
    if (!source) {
      return {
        ok: false,
        reason: 'У перегона нет заявки — лист выписывать не на что',
        blocking: [],
      };
    }
    if (source.status === 'cancelled' || source.status === 'new') {
      return {
        ok: false,
        reason: `Заявка в статусе «${requestStatusLabels[source.status]}» — перегон по ней не выписывают`,
        blocking: [source.displayNumber],
      };
    }
    return { ok: true };
  }
  /*
   * Рейс без заявок. Обычной выписке это отказ: лист рождается из работы по заявкам, и бланк без
   * задания расходовал бы номер строгой отчётности ни на что.
   *
   * По праву `waybills.issueBlank` — не отказ, а решение человека (ADR 0071): диспетчеру нужен
   * пустой бланк на машину и водителя, чтобы задание вписали от руки в дороге. Пустым при этом
   * остаётся только задание — машина, водитель и дата приходят из самого рейса, и лист без них не
   * выписывается ни с правом, ни без.
   */
  if (route.requests.length === 0 && !route.blankAllowed) {
    return { ok: false, reason: BLANK_WAYBILL_DENIED, blocking: [] };
  }
  const blocking = route.requests.filter((r) => r.status !== 'confirmed');
  if (blocking.length > 0) {
    return {
      ok: false,
      reason: 'В маршруте есть заявки не в работе — уберите их из рейса или заведите новый маршрут',
      blocking: blocking.map((r) => r.displayNumber),
    };
  }
  return { ok: true };
}

// ── Коррекция рейса задним числом (ADR 0101) ──

/**
 * Эффективная дата переноса рейса — та, по которой спрашивается право задним числом (§4 плана
 * ADR 0101, Р29). `null` — дата не двигается вовсе, и заднего числа в правке нет: водителя,
 * реквизиты и комментарий прошлого рейса правят без права и без причины (ADR 0101 п. 6).
 *
 * Одна функция на портал и сервер, потому что разойтись им здесь нельзя: разъехавшись в том, что
 * считается сдвигом, форма спрашивала бы причину там, где ручка её не ждёт, — или молча отправляла
 * бы правку, которой ответят 403.
 *
 * Берётся **более ранняя из двух** дат, и это не то же правило, что у правки заявки
 * (`movedRequestDateKey` смотрит только на новые значения). Разница из §4 плана и по существу:
 * заявка переносится сама, и утверждение у неё одно — «работа была в этот день»; перенос же рейса
 * снимает работу с одного дня и ставит на другой, то есть утверждает о **каждом** из них. Взяв
 * только новую дату, мы открыли бы вчерашний рейс любому — достаточно перенести его на завтра, и
 * вчерашняя подача всех его заявок переписалась бы без права и без следа.
 */
export function movedRouteDateKey(before: string, after: string | null | undefined): string | null {
  if (!after || after === before) return null;
  return after < before ? after : before;
}

/**
 * Что мешает исправить рейс; `blocking` — номера заявок, из-за которых коррекция не идёт. Форма та
 * же, что у `IssueWaybillCheck`, но тип свой: проверки разные, и общий тип обещал бы, что один
 * ответ годится вместо другого.
 */
export type RouteCorrectionCheck =
  { ok: true } | { ok: false; reason: string; blocking: readonly string[] };

/**
 * Можно ли корректировать этот рейс — то, что не зависит от права и причины (их спрашивает
 * `backdateGuard`, Р29).
 *
 * Проверок две, и порядок у них тот же, что в `backdateGuard`: первым читается то, что здесь и
 * сейчас не исправить.
 *
 * 1. **Глубина.** Рейс старше `WAYBILL_CORRECTION_DAYS` дней — работа администратора (Р37).
 *    Считается общей функцией `correctionFloorDateKey`, а не вторым выражением с тем же числом:
 *    разъехавшись, портал прятал бы кнопку там, где ручка сработала бы, и наоборот.
 * 2. **Состав.** Все заявки рейса обязаны быть «В работе» (Р3). Правило одно для выписки и для
 *    перевыписки: новый лист печатает задание по **всему** составу, и переписав назначения только
 *    у части заявок, получим бумагу, где остальные талоны снова расходятся с порталом (Р13).
 *
 * Отказ по составу называет номера и говорит, что делать (Р38): диспетчер чинит рейс сам, пока все
 * его заявки в работе, а с закрытой заявкой коррекция становится совместной — откат закрытия
 * делает администратор своим правом (`requests.rollbackStatus`), и внутри коррекции он не
 * подменяется: иначе `waybills.correct` получило бы силу чужого права в обход матрицы, а на вопрос
 * «кто снял закрытие» отвечало бы действие, которого формально никто не совершал.
 *
 * Перегон здесь не разбирается: состава у него нет вовсе, а его заявку-основание проверяет
 * `canIssueWaybill` в preflight будущего состояния (Р36).
 */
export function canCorrectRoute(
  route: {
    /** День рейса — эффективная дата операции по таблице §4 плана. */
    routeDate: string;
    requests: readonly { displayNumber: string; status: RequestStatus }[];
  },
  today: string,
  /** `waybills.correctBeyondLimit` у субъекта: глубина ему не граница (Р37). */
  options: { unlimited?: boolean } = {},
): RouteCorrectionCheck {
  if (options.unlimited !== true && route.routeDate < correctionFloorDateKey(today)) {
    return {
      ok: false,
      reason: `Рейс старше ${WAYBILL_CORRECTION_DAYS} дней — такую давность исправляет администратор, попросите его выполнить коррекцию`,
      blocking: [],
    };
  }
  const blocking = route.requests.filter((r) => r.status !== 'confirmed');
  if (blocking.length > 0) {
    return {
      ok: false,
      reason: routeCorrectionBlockedReason(blocking),
      blocking: blocking.map((r) => r.displayNumber),
    };
  }
  return { ok: true };
}

/**
 * Текст отказа по составу: не «нельзя», а «сделайте вот это» (Р38).
 *
 * Поручения два и они разным людям, поэтому и текст собирается по тому, что в составе: закрытую
 * заявку возвращает в работу администратор, «Новую» проводят обычным порядком — с визой и
 * назначением, — и делает это тот же диспетчер. Один общий текст на оба случая отправил бы
 * половину просителей не к тому человеку.
 */
function routeCorrectionBlockedReason(blocking: readonly { status: RequestStatus }[]): string {
  const parts = [
    'Лист печатает задание по всему составу, поэтому рейс исправляется только целиком',
  ];
  if (blocking.some((r) => r.status === 'done' || r.status === 'cancelled')) {
    parts.push(
      'закрытые и отменённые заявки возвращает в работу администратор — факт при откате сохраняется',
    );
  }
  if (blocking.some((r) => r.status === 'new')) {
    parts.push('новую заявку сначала берут в работу обычным порядком: виза, назначение, маршрут');
  }
  return `${parts.join('; ')}.`;
}

/**
 * Груз в том виде, в каком его печатает бланк: объём, а если его нет — масса. Правило одно на
 * портал и на снимок листа, чтобы карточка рейса и бумага не расходились в единицах.
 */
export function routeCargoLabel(
  volumeM3: string | number | null,
  weightTons: string | number | null,
): string {
  if (volumeM3 !== null && volumeM3 !== '') return `${volumeM3} м³`;
  if (weightTons !== null && weightTons !== '') return `${weightTons} т`;
  return '';
}

/**
 * Сколько знаков комментария печатается в графе «Груз» бланка 4-П.
 *
 * Число снято с бумаги, а не выведено из ширины колонок: графа (объединение `AT75:BF75`) держит
 * около двадцати знаков в строку при кегле 6 pt, а высота строки — ровно две строки, из которых
 * первую занимает сам груз. Двадцать первый знак уезжает на третью строку, которой в строке листа
 * нет, и обрезается по её границе — молча и посреди слова, как это уже было с наименованием
 * машины в ЭСМ-2 (ADR 0060). Поэтому режет портал и ставит многоточие: у обрыва должен быть
 * виден обрыв.
 *
 * Предел с запасом: шрифт пропорциональный, и «ЖЖЖ» шире «ллл» — считать знаки можно только
 * приблизительно, а третья строка не должна появляться и на самой широкой записи.
 */
export const CARGO_NOTE_LIMIT = 20;

/**
 * Груз в том виде, в каком он идёт в бланк: количество первой строкой, комментарий заявки —
 * второй (ADR 0071).
 *
 * Комментарий у заявки свободный и многострочный («песок, звонить за час»), а графа держит одну
 * строку под него — поэтому переводы строк схлопываются, а хвост отрезается многоточием.
 * Собирает строку портал, а не бланк: склейка плейсхолдеров в ячейке оставила бы у заявки без
 * комментария пустую вторую строку, а у заявки без груза — начинающуюся с переноса графу.
 */
export function routeCargoWithNote(cargo: string, comment: string): string {
  const note = comment.replace(/\s+/gu, ' ').trim();
  const short =
    note.length > CARGO_NOTE_LIMIT ? `${note.slice(0, CARGO_NOTE_LIMIT - 1).trimEnd()}…` : note;
  return [cargo.trim(), short].filter((part) => part !== '').join('\n');
}

/**
 * ФИО ответственного так, как его пишет бланк: фамилия с инициалами. В заявке человека называют
 * полностью, а графа держит около полусотни знаков в строку и ровно две строки по высоте —
 * «Кузнецова Анна Владимировна, +7 914 123-45-67» переносится на вторую строку и вытесняет с
 * бумаги контакт разгрузки.
 *
 * Само сокращение живёт в `person-name.ts` (`formatNameWithInitials`): тем же видом печатается
 * расшифровка подписи водителя в 4-П и форме № 3, и два написания одного правила разошлись бы на
 * первой же правке — например, на записи из четырёх слов.
 */
const contactNameLabel = formatNameWithInitials;

/**
 * Контакты задания в том виде, в каком их печатает графа «заказчик, телефон» бланка 4-П: строка
 * на каждый конец маршрута — кто отдаёт груз и кто его принимает.
 *
 * Строки не подписаны «погрузка/разгрузка»: порядок тот же, что у граф «откуда» и «куда» той же
 * строки задания, а подпись вытеснила бы из графы телефон. Пустой контакт пропускается целиком, а
 * не печатается пустой строкой: у заявок старше миграции 0062 контакта нет вовсе, и одинокая
 * запятая читалась бы как потерянный номер.
 */
export function routeContactsLabel(contacts: readonly { name: string; phone: string }[]): string {
  return contacts
    .map(({ name, phone }) =>
      // Номер печатается тем же видом, что и везде (ADR 0066): в бланке его читают и набирают.
      [contactNameLabel(name), formatPhone(phone.trim())].filter((part) => part !== '').join(', '),
    )
    .filter((line) => line !== '')
    .join('\n');
}

/**
 * Задание рейсов 5–7 одной строкой — так, как их печатает блок «Дополнительное задание водителю»
 * бланка 4-П (ADR 0068).
 *
 * Графы там нет ни одной: три нижние строки блока — это три объединённые ячейки во всю ширину, и
 * «откуда», «куда», груз и контакты приходится складывать в строку самим. Порядок тот же, что у
 * граф таблицы выше, — водитель читает обе части бланка подряд, и менять местами их нельзя.
 *
 * Номер рейса в строку не идёт: в четырёх строках таблицы его тоже не печатают, а рядом с адресом
 * он читался бы как номер дома. Собирается строка на сервере, а не склейкой плейсхолдеров в
 * ячейке: у пустого рейса из бланка вышли бы осиротевшие стрелка и запятые.
 *
 * Контакты приходят готовой подписью `routeContactsLabel` — двумя строками, погрузка и разгрузка.
 * Здесь они сводятся в одну через «; »: строк у ячейки две, и перенос внутри контактов вытеснил бы
 * с бумаги вторую половину задания. Тем же порядком сводится груз: с комментарием заявки он идёт
 * двумя строками (`routeCargoWithNote`), а здесь строка одна на всё задание.
 */
export function routeExtraTaskLine(task: {
  from: string;
  to: string;
  cargo: string;
  contacts: string;
}): string {
  const oneLine = (value: string): string =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '')
      .join('; ');
  const route = [task.from.trim(), task.to.trim()].filter((part) => part !== '').join(' → ');
  return [route, oneLine(task.cargo), oneLine(task.contacts)]
    .filter((part) => part !== '')
    .join(', ');
}

// ── Схемы ──

/** Версия маршрута: оптимистическая блокировка, как у заявки. */
const versionSchema = z.coerce.number().int().min(0);

/**
 * Откуда и куда едет перегон. Свободная строка, а не адрес из подсказок: техника уходит с базы, с
 * прошлой площадки или из ремонта, и половина этих мест в ФИАС не значится.
 */
const moveLocationSchema = z.string().trim().min(1, 'Укажите место').max(1000);

export const createVehicleRouteSchema = z
  .object({
    vehicleId: uuidSchema,
    routeDate: dateOnlySchema,
    /** Водителя назначают и позже: маршрут собирают заранее, человека ставят утром. */
    driverPersonId: uuidSchema.nullable().optional(),
    trip: routeTripFieldsSchema.optional(),
    comment: z.string().trim().max(2000).optional().default(''),
    /**
     * Причина заведения рейса задним числом (ADR 0101 п. 4, дыра 1 плана). До ADR 0101 рейс на
     * прошедшую дату заводился без единой проверки — ни права, ни причины, ни следа.
     *
     * Необязательна в схеме и обязательна на сервере ровно тогда, когда `routeDate` уже прошла:
     * решает это `backdateGuard`, который один знает субъекта, его права и глубину. Схема субъекта
     * не знает вовсе, и требовать причину у всякого рейса значило бы спрашивать объяснение за
     * обычный завтрашний день.
     *
     * Отдельного ключа идемпотентности здесь нет намеренно: рейс — планировочная запись, номер
     * строгой отчётности он не жжёт, а повторно заведённый лишний рейс удаляется. Ключ появляется
     * там, где рождается документ, — у выписки листа (`issueRouteWaybillSchema`).
     */
    reason: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateVehicleRouteInput = z.infer<typeof createVehicleRouteSchema>;
export type CreateVehicleRouteBody = z.input<typeof createVehicleRouteSchema>;

export const updateVehicleRouteSchema = z
  .object({
    /**
     * День рейса. Меняется вместе с составом: заявка едет в тот день, в который заведён рейс, и
     * лист печатает задание на него (`canJoinRoute`). Поэтому сервер переносит и время подачи
     * заявок рейса — иначе рейс и его заявки разошлись бы по разным дням, а бумага напечатала бы
     * работу, которой в этот день никто не заказывал.
     */
    routeDate: dateOnlySchema.optional(),
    driverPersonId: uuidSchema.nullable().optional(),
    trip: routeTripFieldsSchema.optional(),
    comment: z.string().trim().max(2000).optional(),
    /** Задание перегона: правится, пока лист не выписан. У грузового рейса сервер их не примет. */
    moveFrom: moveLocationSchema.optional(),
    moveTo: moveLocationSchema.optional(),
    version: versionSchema,
    /**
     * Причина переноса дня задним числом (ADR 0101 п. 4 и 6, Р29).
     *
     * Спрашивается **только за дату** и только тогда, когда сдвиг задевает прошедший день
     * (`movedRouteDateKey`). Водитель, реквизиты выезда и комментарий вчерашнего рейса правятся
     * без права и без причины: пока листа нет, рейс — планировочная запись, и запирать её правку
     * значило бы запирать подготовку к самой коррекции. Дата — другое дело: `moveRouteToDate`
     * переписывает `scheduledAt` всех грузовых заявок состава, то есть двигает календарь заказчика
     * тем же движением, что и защищённый `PATCH /vehicle-requests/:id`.
     *
     * Необязательна в схеме, потому что схема не знает ни субъекта, ни его прав, ни того, куда
     * дату двигают: решает это `backdateGuard` в обработчике. Требовать причину у всякой правки
     * рейса значило бы спрашивать объяснение за обычный перенос завтрашнего выезда.
     *
     * Ключа идемпотентности здесь нет намеренно — и по той же причине, по какой его нет у
     * заведения рейса (`createVehicleRouteSchema`): номеров строгой отчётности перенос не
     * расходует. Действующий лист правку запрещает целиком (`isRouteEditable`), значит сгореть
     * нечему, повтор после обрыва связи лишь ещё раз поставит ту же дату, а строка
     * `waybill_corrections` без единого листа засоряла бы журнал коррекций тем, чего в нём не
     * ищут. Объяснение уходит в аудит (`vehicle_route.update`, поля `backdated` и `reason`).
     */
    reason: z.string().trim().max(2000).optional(),
  })
  .strict();
export type UpdateVehicleRouteInput = z.infer<typeof updateVehicleRouteSchema>;
export type UpdateVehicleRouteBody = z.input<typeof updateVehicleRouteSchema>;

/**
 * Перегон техники по заявке: доставка на объект или вывоз с него (миграция 0082).
 *
 * Заводится по желанию — портал не знает, поедет техника своим ходом или тралом, и решать это ему
 * нечем. Машина не спрашивается: её несёт назначение заявки, и «перегнать одну, а работать другой»
 * — не состояние, а расхождение.
 *
 * Дата отдельная: у заявки спецтехники период работ, а перегон — один день внутри него (обычно
 * первый и последний, но техника приезжает и накануне).
 */
export const createRelocationRouteSchema = z
  .object({
    purpose: z.enum(['delivery', 'pickup']),
    routeDate: dateOnlySchema,
    driverPersonId: uuidSchema.nullable().optional(),
    moveFrom: moveLocationSchema,
    moveTo: moveLocationSchema,
    trip: routeTripFieldsSchema.optional(),
    comment: z.string().trim().max(2000).optional().default(''),
  })
  .strict();
export type CreateRelocationRouteInput = z.infer<typeof createRelocationRouteSchema>;
export type CreateRelocationRouteBody = z.input<typeof createRelocationRouteSchema>;

/**
 * Положить заявку в рейс или перенести её из другого.
 *
 * Исходный маршрут опознаётся парой «кто + версия», а не одной версией: версии нумеруются в каждом
 * маршруте отдельно, и после конкурентного переноса в третий маршрут одинокая версия совпала бы
 * случайно. Сервер проверяет, что заявка **сейчас** лежит именно в `source.routeId`.
 */
export const attachRouteRequestSchema = z
  .object({
    requestId: uuidSchema,
    version: versionSchema,
    source: z.object({ routeId: uuidSchema, version: versionSchema }).strict().optional(),
  })
  .strict();
export type AttachRouteRequestInput = z.infer<typeof attachRouteRequestSchema>;
export type AttachRouteRequestBody = z.input<typeof attachRouteRequestSchema>;

/**
 * Новый порядок заявок — полным списком, а не парой «подвинуть вверх»: сервер переписывает талоны
 * целиком, и две одновременные перестановки не соберут дыр в нумерации.
 */
export const routeOrderSchema = z
  .object({
    requestIds: z.array(uuidSchema).min(1).max(MAX_ROUTE_REQUESTS),
    version: versionSchema,
  })
  .strict()
  .refine((v) => new Set(v.requestIds).size === v.requestIds.length, {
    message: 'Заявка не может стоять в рейсе дважды',
    path: ['requestIds'],
  });
export type RouteOrderInput = z.infer<typeof routeOrderSchema>;
export type RouteOrderBody = z.input<typeof routeOrderSchema>;

/**
 * Выписка листа и изъятие заявки версию тоже сверяют — состав рейса меняют оба.
 *
 * Причина и ключ операции нужны выписке **на прошедший день** (ADR 0101 п. 4, дыра 1 плана): до
 * ADR 0101 эта ручка про дату не спрашивала вовсе, и вчерашний рейс выписывал лист молча — без
 * права, без объяснения и без метки в журнале. Теперь такой лист рождается операцией `issue`:
 * причина уходит в `correction_reason` при пустом `corrects_waybill_id` (Р35), а ключ спасает от
 * второго сгоревшего номера после обрыва связи (Р31).
 *
 * Оба поля необязательны, потому что обычная дневная выписка ни тем, ни другим не является:
 * требовать под неё ключ и объяснение значило бы объявить коррекцией всю работу диспетчера.
 * Спрашивает их сервер ровно тогда, когда дата рейса уже прошла.
 */
/**
 * Код отказа «предупреждения не подтверждены» (Р21). Отдельным кодом, а не текстом: по нему портал
 * открывает окно подтверждения со свежим списком, а `version_conflict` он показывает как «откройте
 * заново» — два разных исхода на один код различить было бы нечем.
 */
export const WAYBILL_ACK_REQUIRED_CODE = 'waybill_ack_required';

/**
 * Тело отказа `waybill_ack_required` (Р21): то, из чего портал собирает окно подтверждения.
 *
 * Отдельным полем `details`, а не текстом сообщения: список читает человек, а отпечаток
 * возвращается серверу нетронутым. `fields` для этого не годится — там `Record<string, string>` под
 * пометки полей формы, и массив предупреждений в него не кладётся.
 *
 * `routeId` назван явно ради переноса между рейсами (Р21а): подтверждаются **два** листа, и первый
 * же неподтверждённый останавливает операцию — без имени рейса окно не сказало бы, о чьей бумаге
 * речь.
 */
export interface WaybillAckRequiredDetails {
  routeId: string;
  /** «Р-12» — им окно называет рейс человеку. */
  routeNumber: string;
  /** `sha256` от каноникализованных фактов: его же портал возвращает в `acknowledge`. */
  fingerprint: string;
  warnings: WaybillWarning[];
}

/**
 * Рукопожатие выписки (Р21): отпечаток набора предупреждений, который человек видел и подтвердил.
 *
 * Отпечаток, а не «да, согласен»: между показом окна и нажатием кнопки набор меняется — водителю
 * внесли СНИЛС, диспетчер поправил адрес точки, соседняя вкладка добавила заявку с другим объектом
 * затрат, — и подтверждение обязано относиться к тому положению дел, которое человек прочитал.
 * Считает его сервер (`sha256` от `canonicalWarningPayload`), браузер только возвращает обратно.
 *
 * Одна схема на все четыре пути выпуска номера (Р21а): у обычной выписки, задней выписки, коррекции
 * рейса и переноса заявки между рейсами подтверждается одно и то же — набор предупреждений рейса, —
 * и разные формы поля заставили бы каждый путь помнить свою.
 */
export const waybillAcknowledgeSchema = z
  .object({ fingerprint: z.string().trim().min(1).max(128) })
  .strict();
export type WaybillAcknowledgeInput = z.infer<typeof waybillAcknowledgeSchema>;

export const issueRouteWaybillSchema = z
  .object({
    version: versionSchema,
    reason: z.string().trim().max(2000).optional(),
    operationId: uuidSchema.optional(),
    /**
     * Подтверждение предупреждений (Р21) — **в дополнение** к полям коррекции, а не вместо них:
     * задняя выписка объясняется причиной и опознаётся ключом операции, и подтверждает при этом ту
     * же бумагу, что и дневная.
     *
     * Необязательно, потому что пустой набор рукопожатия не требует вовсе: лист без единого
     * предупреждения выписывается одним нажатием, как и до Р21.
     */
    acknowledge: waybillAcknowledgeSchema.optional(),
  })
  .strict();
export type IssueRouteWaybillInput = z.infer<typeof issueRouteWaybillSchema>;

// ── Схемы коррекции (ADR 0101) ──

/**
 * Ключ идемпотентности операции (Р31): uuid, который клиент придумывает **до** отправки.
 *
 * Обязателен во всех командах коррекции. Повтор после сетевого таймаута обязан вернуть результат
 * прежней операции, а не сжечь следующий номер серии: у бланка строгой отчётности «повторить
 * запрос» не бесплатное действие, и молчаливый дубль виден только в журнале — разрывом нумерации,
 * который потом никто не объяснит.
 */
const operationIdSchema = uuidSchema;

/**
 * Причина коррекции. Обязательна и не пустая: она уходит в запись операции (Р16) и печатается в
 * обоих листах — в старом как причина аннулирования, в новом как причина коррекции (Р35). Именно
 * она отвечает через два месяца на вопрос «почему за 12 августа два номера».
 */
const correctionReasonSchema = z.string().trim().min(1, 'Укажите причину').max(2000);

/**
 * Исправление исполнения рейса (Р2): одно событие, а не пять.
 *
 * Сервер в одной транзакции аннулирует действующий лист, переписывает машину и водителя рейса,
 * тянет за ними назначения **всех** заявок состава, снимает подтверждение затронутых смен и
 * выписывает новый лист. Машина принимается только здесь: обычная правка рейса её по-прежнему не
 * берёт (ADR 0082 п. 5), потому что «поедет другой машиной» в будущем — это другое назначение, а в
 * прошедшем дне рейс состоялся один, и ехала им одна машина.
 *
 * `requestOrder` меняет только порядок талонов внутри рейса. Состав коррекцией не пополняется и не
 * сокращается: приход и уход заявки — это перенос между рейсами со своей командой, своими
 * блокировками и своей версией исходного рейса (`transferCorrectionSchema`). Прежний `requestIds`
 * отвергнут именно поэтому: он позволял потерять талон в чужом рейсе, не назвав его.
 *
 * Водитель — `uuid`, а не `nullable`: пустой водитель у обычного рейса законен (маршрут собирают
 * заранее), но лист без него не выписывается ни с правом, ни без, а коррекция всегда кончается
 * новым листом.
 */
export const correctRouteSchema = z
  .object({
    operationId: operationIdSchema,
    version: versionSchema,
    vehicleId: uuidSchema.optional(),
    driverPersonId: uuidSchema.optional(),
    trip: routeTripFieldsSchema.optional(),
    requestOrder: z.array(uuidSchema).min(1).max(MAX_ROUTE_REQUESTS).optional(),
    reason: correctionReasonSchema,
    /**
     * Подтверждение предупреждений нового листа (Р21а). Коррекция кончается настоящим бланком, и
     * рукопожатие у неё то же самое: пропусти его здесь — и лист, рождённый коррекцией, обошёл бы
     * проверку, которую проходит обычная выписка.
     *
     * Отпечаток считается по **будущему** состоянию рейса — после смены машины, водителя и порядка
     * талонов: подтверждают то, что напечатается, а не то, что было до операции.
     */
    acknowledge: waybillAcknowledgeSchema.optional(),
  })
  .strict()
  .refine((v) => !v.requestOrder || new Set(v.requestOrder).size === v.requestOrder.length, {
    message: 'Заявка не может стоять в рейсе дважды',
    path: ['requestOrder'],
  })
  /*
   * Пустая коррекция отклоняется (Р31): тело с одной причиной сожгло бы номер, ничего не исправив.
   * Схема ловит только заведомо пустое — назвать ли переданные значения новыми, знает лишь сервер,
   * сравнивая их с текущим состоянием рейса под блокировкой. Дальше он же и откажет, но начинать
   * транзакцию ради тела, в котором менять нечего в принципе, незачем.
   */
  .refine(
    (v) =>
      v.vehicleId !== undefined ||
      v.driverPersonId !== undefined ||
      v.trip !== undefined ||
      v.requestOrder !== undefined,
    { message: 'Коррекция должна что-то менять: машину, водителя, реквизиты или порядок талонов' },
  );
export type CorrectRouteInput = z.infer<typeof correctRouteSchema>;
export type CorrectRouteBody = z.input<typeof correctRouteSchema>;

/**
 * Перенос заявки между рейсами прошедших дней (Р30): «оформили средой, а ехали вторником».
 *
 * `:id` в пути — рейс-**приёмник**, `source` — рейс-источник со своей версией. Обе версии
 * спрашиваются, потому что коррекция трогает оба рейса и жжёт оба номера: одной версии хватило бы
 * ровно до первой встречной правки чужого рейса. Заявка называется одна — состав меняется на один
 * талон, и «перенести пачкой» здесь не заводится намеренно: каждый талон это отдельная строка
 * задания в двух бумагах сразу.
 *
 * Сервер одной транзакцией аннулирует оба действующих листа, переносит заявку, уплотняет талоны
 * источника, переписывает назначение на машину приёмника и выписывает листы обоих рейсов — второй
 * только если источник не опустел (Р22: пустой бланк на опустевший рейс не выписывается). Заявка
 * ни на миг не остаётся в работе без маршрута — ради этого команда и атомарна.
 */
export const transferCorrectionSchema = z
  .object({
    operationId: operationIdSchema,
    /** Версия приёмника — того рейса, чей `:id` стоит в пути. */
    version: versionSchema,
    source: z.object({ routeId: uuidSchema, version: versionSchema }).strict(),
    requestId: uuidSchema,
    /** Талон в приёмнике, 1..7; не передан — заявка встаёт последней. */
    position: z.number().int().min(1).max(MAX_ROUTE_REQUESTS).optional(),
    reason: correctionReasonSchema,
    /**
     * Подтверждения обоих листов (Р21а). Двумя полями, а не одним: листов здесь два, предупреждения
     * у них разные — заявка уехала из одного задания в другое, — и отпечаток считается по каждому
     * рейсу отдельно. Один общий отпечаток означал бы, что подтверждение приёмника закрывает и
     * бумагу источника, которой человек не видел.
     *
     * Обе половины необязательны, потому что пустой набор рукопожатия не требует: у рейса без
     * предупреждений подтверждать нечего, и чаще всего такова хотя бы одна из сторон.
     */
    acknowledge: z
      .object({
        target: waybillAcknowledgeSchema.optional(),
        source: waybillAcknowledgeSchema.optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type TransferCorrectionInput = z.infer<typeof transferCorrectionSchema>;
export type TransferCorrectionBody = z.input<typeof transferCorrectionSchema>;

export const routeVersionQuerySchema = z.object({ version: versionSchema });
export type RouteVersionQuery = z.infer<typeof routeVersionQuerySchema>;

/**
 * Маршрут при переводе заявки в работу: существующий рейс этой машины на эту дату либо новый.
 * Ровно одно из двух — «и то, и другое» означало бы два разных ответа на вопрос, куда едет заявка.
 *
 * У готового рейса спрашивается и водитель (ADR 0048). Смена назначенной техники переставляет
 * заявку в рейс новой машины, и человек за рулём меняется вместе с ней — а до сих пор сменить его
 * этим же действием было нечем: оставалось второе окно правки рейса (ADR 0082) либо заведение
 * лишнего маршрута «с нужным водителем». Отвергнута как раз вторая дорога: маршрут заводили не
 * ради маршрута, и в плане дня оставалась пустая запись, которую потом убирали руками.
 *
 * Состояния поля три, а не два, и различаются они ровно так же, как у правки рейса
 * (`updateVehicleRouteSchema.driverPersonId`) — один и тот же вопрос не должен иметь двух ответов:
 *
 * - поля нет — водителя не трогать. Умолчание именно такое, потому что рейс общий: в нём стоят
 *   заявки других объектов, и «не сказали — значит снять» стирало бы водителя чужой работы при
 *   каждой смене техники;
 * - `null` — снять водителя: рейс остаётся, за рулём пока никого;
 * - идентификатор — поставить этого человека, и он поедет за всех, кто стоит в рейсе.
 *
 * У новой ветки водитель без `null` — снимать там нечего: рейс рождается этим же запросом, и
 * «завести рейс и тут же снять с него водителя» это просто рейс без водителя.
 *
 * С машинистом заявки (`assignVehicleSchema.driverPersonId`) это разные графы и разные бумаги:
 * тот стоит в недельном листе ЭСМ-2 заказа техники на объект, этот — за рулём рейса. Прежде здесь
 * стояло «где выписывается ЭСМ-2, рейса не бывает вовсе, и наоборот» — с приходом дневных 4-П на
 * весь период это стало неправдой: у одного заказа за один день бывают обе бумаги сразу — недельный
 * ЭСМ-2 и дневной лист его рейса (решение 1 ADR 0041 в редакции ADR 0207). Графы от этого не
 * сливаются, а расходятся ещё заметнее: неделю подписывает машинист заявки, дневной рейс везёт
 * водитель этого дня, и разными людьми они бывают законно. Расхождение портал называет вслух —
 * молча подставить одного вместо другого нельзя ни в ту, ни в другую сторону.
 */
export const assignRouteSchema = z.union([
  z.object({ routeId: uuidSchema, driverPersonId: uuidSchema.nullable().optional() }).strict(),
  z
    .object({
      newRoute: z
        .object({
          driverPersonId: uuidSchema.optional(),
          trip: routeTripFieldsSchema.optional(),
        })
        .strict(),
    })
    .strict(),
]);
export type AssignRouteInput = z.infer<typeof assignRouteSchema>;
export type AssignRouteBody = z.input<typeof assignRouteSchema>;

export const VEHICLE_ROUTE_SORT_FIELDS = ['routeDate', 'num', 'createdAt'] as const;

export const vehicleRouteListQuerySchema = baseListQuery(VEHICLE_ROUTE_SORT_FIELDS).extend({
  /** Период рейсов: маршруты читают по дням, а не всей историей сразу. */
  dateFrom: dateOnlySchema.optional(),
  dateTo: dateOnlySchema.optional(),
  vehicleId: uuidSchema.optional(),
  driverPersonId: uuidSchema.optional(),
  num: z.coerce.number().int().positive().optional(),
  /** Состояние документа: «ещё не выписан» — то, чем диспетчер закрывает день. */
  waybill: z.enum(['none', 'issued']).optional(),
  /** Есть ли куда положить ещё одну заявку — форма перевода в работу спрашивает только такие. */
  hasFreeSlots: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type VehicleRouteListQuery = z.infer<typeof vehicleRouteListQuerySchema>;
